import { z } from 'zod';

/**
 * Warehouse Sync Job Logging System
 *
 * Provides complete audit trail for warehouse sync operations with two implementations:
 * - ConsoleJobLogger: For CLI usage with console output
 * - AxiomJobLogger: For API usage with real-time streaming to Axiom
 */

// Type definition for Axiom Logger (matches @axiomhq/logging Logger interface)
export interface AxiomLogger {
  info(message: string, data?: Record<string, unknown>): void;
  warn(message: string, data?: Record<string, unknown>): void;
  error(message: string, data?: Record<string, unknown>): void;
  flush(): Promise<void>;
}

export interface IWarehouseSyncLogger {
  info(message: string, metadata?: Record<string, unknown>): void;
  warn(message: string, metadata?: Record<string, unknown>): void;
  error(
    message: string,
    error?: unknown,
    metadata?: Record<string, unknown>,
  ): void;
  logInvalidRecord(
    entityType: string,
    record: unknown,
    errors: z.ZodError,
  ): void;
  getInvalidCount(entityType: string): number;
  getTotalInvalidCount(): number;
  finalize(): Promise<void>;
}

/**
 * Console-based logger for CLI usage
 * Outputs human-readable logs to console
 */
export class ConsoleJobLogger implements IWarehouseSyncLogger {
  private invalidCounts: Record<string, number> = {
    states: 0,
    counties: 0,
    owners: 0,
    vendors: 0,
    lettings: 0,
    items: 0,
    proposals: 0,
    proposalItems: 0,
    bids: 0,
    bidItems: 0,
  };

  private formatTimestamp(): string {
    return new Date().toISOString().replace('T', ' ').substring(0, 19);
  }

  info(message: string, metadata?: Record<string, unknown>): void {
    const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : '';
    console.log(`[${this.formatTimestamp()}] INFO: ${message}${metaStr}`);
  }

  warn(message: string, metadata?: Record<string, unknown>): void {
    const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : '';
    console.log(`[${this.formatTimestamp()}] WARN: ${message}${metaStr}`);
  }

  error(
    message: string,
    error?: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : '';
    const err = error instanceof Error ? error : new Error(String(error));
    const errorStr = error ? ` - ${err.message}` : '';
    console.error(
      `[${this.formatTimestamp()}] ERROR: ${message}${errorStr}${metaStr}`,
    );
  }

  logInvalidRecord(
    entityType: string,
    _record: unknown,
    _errors: z.ZodError,
  ): void {
    this.invalidCounts[entityType] = (this.invalidCounts[entityType] || 0) + 1;
  }

  getInvalidCount(entityType: string): number {
    return this.invalidCounts[entityType] || 0;
  }

  getTotalInvalidCount(): number {
    return Object.values(this.invalidCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
  }

  async finalize(): Promise<void> {
    // No-op for console logger
  }
}

/**
 * Axiom-based logger for API usage
 * Streams logs in real-time to Axiom for queryable job tracking
 */
export class AxiomJobLogger implements IWarehouseSyncLogger {
  private invalidCounts: Record<string, number> = {
    states: 0,
    counties: 0,
    owners: 0,
    vendors: 0,
    lettings: 0,
    items: 0,
    proposals: 0,
    proposalItems: 0,
    bids: 0,
    bidItems: 0,
  };

  constructor(
    private readonly jobId: string,
    private readonly organizationId: string,
    private readonly axiomLogger: AxiomLogger,
    private readonly environment: string = 'local',
  ) {}

  private async log(
    level: 'info' | 'warn' | 'error',
    message: string,
    metadata: Record<string, unknown> = {},
    error?: unknown,
  ): Promise<void> {
    let err: Error | undefined;
    if (error instanceof Error) {
      err = error;
    } else if (error) {
      err = new Error(String(error));
    }

    const logEntry = {
      jobId: this.jobId,
      jobType: 'warehouse-sync',
      organizationId: this.organizationId,
      level,
      message,
      timestamp: new Date().toISOString(),
      environment: this.environment,
      ...metadata,
      ...(err && {
        error: {
          message: err.message,
          stack: err.stack,
          name: err.name,
        },
      }),
    };

    if (level === 'error') {
      this.axiomLogger.error(message, logEntry);
    } else if (level === 'warn') {
      this.axiomLogger.warn(message, logEntry);
    } else {
      this.axiomLogger.info(message, logEntry);
    }

    await this.axiomLogger.flush();
  }

  info(message: string, metadata?: Record<string, unknown>): void {
    this.log('info', message, metadata);
  }

  warn(message: string, metadata?: Record<string, unknown>): void {
    this.log('warn', message, metadata);
  }

  error(
    message: string,
    error?: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    this.log('error', message, metadata, error);
  }

  logInvalidRecord(
    entityType: string,
    record: unknown,
    errors: z.ZodError,
  ): void {
    this.invalidCounts[entityType] = (this.invalidCounts[entityType] || 0) + 1;

    this.log('warn', `Invalid ${entityType} record detected`, {
      entityType,
      validationErrors: errors.format(),
      record,
    });
  }

  getInvalidCount(entityType: string): number {
    return this.invalidCounts[entityType] || 0;
  }

  getTotalInvalidCount(): number {
    return Object.values(this.invalidCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
  }

  async finalize(): Promise<void> {
    await this.axiomLogger.flush();
  }
}

export interface SSEStream {
  writeSSE(message: {
    data: string;
    event?: string;
    id?: string;
  }): Promise<void>;
}

/**
 * SSE + Axiom dual logger for API usage with real-time streaming
 * Sends logs to both Axiom and SSE stream simultaneously
 */
export class SSEAxiomJobLogger implements IWarehouseSyncLogger {
  private invalidCounts: Record<string, number> = {
    states: 0,
    counties: 0,
    owners: 0,
    vendors: 0,
    lettings: 0,
    items: 0,
    proposals: 0,
    proposalItems: 0,
    bids: 0,
    bidItems: 0,
  };

  constructor(
    private readonly jobId: string,
    private readonly organizationId: string,
    private readonly axiomLogger: AxiomLogger,
    private readonly sseStream: SSEStream,
    private readonly environment: string = 'local',
  ) {}

  private async log(
    level: 'info' | 'warn' | 'error',
    message: string,
    metadata: Record<string, unknown> = {},
    error?: unknown,
  ): Promise<void> {
    let err: Error | undefined;
    if (error instanceof Error) {
      err = error;
    } else if (error) {
      err = new Error(String(error));
    }

    const logEntry = {
      jobId: this.jobId,
      jobType: 'warehouse-sync',
      organizationId: this.organizationId,
      level,
      message,
      timestamp: new Date().toISOString(),
      environment: this.environment,
      ...metadata,
      ...(err && {
        error: {
          message: err.message,
          stack: err.stack,
          name: err.name,
        },
      }),
    };

    if (level === 'error') {
      this.axiomLogger.error(message, logEntry);
    } else if (level === 'warn') {
      this.axiomLogger.warn(message, logEntry);
    } else {
      this.axiomLogger.info(message, logEntry);
    }

    await this.axiomLogger.flush();

    await this.sseStream.writeSSE({
      event: 'log',
      data: JSON.stringify(logEntry),
      id: String(Date.now()),
    });
  }

  info(message: string, metadata?: Record<string, unknown>): void {
    this.log('info', message, metadata);
  }

  warn(message: string, metadata?: Record<string, unknown>): void {
    this.log('warn', message, metadata);
  }

  error(
    message: string,
    error?: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    this.log('error', message, metadata, error);
  }

  logInvalidRecord(
    entityType: string,
    record: unknown,
    errors: z.ZodError,
  ): void {
    this.invalidCounts[entityType] = (this.invalidCounts[entityType] || 0) + 1;

    this.log('warn', `Invalid ${entityType} record detected`, {
      entityType,
      validationErrors: errors.format(),
      record,
    });
  }

  getInvalidCount(entityType: string): number {
    return this.invalidCounts[entityType] || 0;
  }

  getTotalInvalidCount(): number {
    return Object.values(this.invalidCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
  }

  async finalize(): Promise<void> {
    await this.axiomLogger.flush();
  }
}

/**
 * Validation schemas for Snowflake data
 */

const dateOrStringSchema = z
  .union([z.string(), z.date(), z.number()])
  .optional()
  .nullable();

const dateRequiredSchema = z
  .union([z.string().min(1), z.date(), z.number()])
  .refine((val) => val != null && val !== '', 'date required');

export const strictSnowflakeOwnerSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    name: z.string().min(1, 'name required').max(255, 'name too long'),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
  })
  .passthrough();

export const strictSnowflakeVendorSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    name: z.string().min(1, 'name required').max(255, 'name too long'),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
    ownerExternalId: z.string().min(1, 'ownerExternalId required'),
  })
  .passthrough();

export const strictSnowflakeLettingSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    date: dateRequiredSchema,
    state: z.string().min(1, 'state required').max(50, 'state too long'),
    proposalCount: z.number().int().nonnegative(),
    ownerExternalId: z.string().min(1, 'ownerExternalId required'),
  })
  .passthrough();

export const strictSnowflakeItemSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    number: z.string().min(1, 'number required').max(255, 'number too long'),
    description: z.string().nullable().optional(),
    category: z.string().max(255, 'category too long').nullable().optional(),
    unit: z.string().max(50, 'unit too long').nullable().optional(),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
    ownerExternalId: z.string().min(1, 'ownerExternalId required'),
  })
  .passthrough();

export const strictSnowflakeProposalSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    callNumber: z
      .string()
      .min(1, 'callNumber required')
      .max(255, 'callNumber too long'),
    completionCalendarDays: z.number().int().nullable().optional(),
    completionDate: dateOrStringSchema,
    completionWorkingDays: z.number().int().nullable().optional(),
    contractId: z
      .string()
      .max(255, 'contractId too long')
      .nullable()
      .optional(),
    contractType: z
      .string()
      .max(255, 'contractType too long')
      .nullable()
      .optional(),
    costEstimate: z.union([z.string(), z.number()]).nullable().optional(),
    description: z.string().nullable().optional(),
    districtCode: z
      .string()
      .max(255, 'districtCode too long')
      .nullable()
      .optional(),
    districtName: z
      .string()
      .max(255, 'districtName too long')
      .nullable()
      .optional(),
    lettingDate: dateOrStringSchema,
    projectNumber: z
      .string()
      .max(255, 'projectNumber too long')
      .nullable()
      .optional(),
    workType: z.string().nullable().optional(),
    route: z.string().max(255, 'route too long').nullable().optional(),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
    bidderCount: z.number().int().nullable().optional(),
    hasAwarded: z.boolean(),
    hasBidTabulation: z.boolean(),
    lettingExternalId: z.string().min(1, 'lettingExternalId required'),
    ownerExternalId: z.string().min(1, 'ownerExternalId required'),
  })
  .passthrough();

export const strictSnowflakeProposalItemSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    proposalExternalId: z.string().min(1, 'proposalExternalId required'),
    itemExternalId: z.string().min(1, 'itemExternalId required'),
    lineNumber: z.number().int().nullable().optional(),
    itemNumber: z
      .string()
      .max(255, 'itemNumber too long')
      .nullable()
      .optional(),
    quantity: z
      .union([z.string(), z.number()])
      .refine((val) => val != null && val !== '', 'quantity required'),
    costEstimate: z.union([z.string(), z.number()]).nullable().optional(),
    description: z.string().nullable().optional(),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
  })
  .passthrough();

export const strictSnowflakeBidSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    vendorExternalId: z.string().min(1, 'vendorExternalId required'),
    proposalExternalId: z.string().min(1, 'proposalExternalId required'),
    deviation: z.union([z.string(), z.number()]).nullable().optional(),
    value: z
      .union([z.string(), z.number()])
      .refine((val) => val != null && val !== '', 'value required'),
    winner: z.boolean().nullable().optional(),
    rank: z.number().int().nullable().optional(),
    extensionTotal: z.union([z.string(), z.number()]).nullable().optional(),
    timeAdjustment: z.union([z.string(), z.number()]).nullable().optional(),
    evaluationAdjustment: z
      .union([z.string(), z.number()])
      .nullable()
      .optional(),
    adjustedTotal: z.union([z.string(), z.number()]).nullable().optional(),
    deviationPercent: z.union([z.string(), z.number()]).nullable().optional(),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
  })
  .passthrough();

export const strictSnowflakeBidItemSchema = z
  .object({
    externalId: z.string().min(1, 'externalId required'),
    bidExternalId: z.string().min(1, 'bidExternalId required'),
    proposalItemExternalId: z
      .string()
      .min(1, 'proposalItemExternalId required'),
    quantity: z
      .union([z.string(), z.number()])
      .refine((val) => val != null && val !== '', 'quantity required'),
    lineNumber: z.number().int({ message: 'lineNumber must be integer' }),
    unitPrice: z
      .union([z.string(), z.number()])
      .refine((val) => val != null && val !== '', 'unitPrice required'),
    extension: z
      .union([z.string(), z.number()])
      .refine((val) => val != null && val !== '', 'extension required'),
    state: z.string().min(1, 'state required').max(50, 'state too long'),
  })
  .passthrough();

export interface ValidationResult<T> {
  valid: T[];
  invalid: Array<{
    record: unknown;
    errors: z.ZodError;
  }>;
}

export function validateRecords<T>(
  records: unknown[],
  schema: z.ZodSchema<T>,
  logger: IWarehouseSyncLogger,
  entityType: string,
): ValidationResult<T> {
  const valid: T[] = [];
  const invalid: Array<{ record: unknown; errors: z.ZodError }> = [];

  for (const record of records) {
    const result = schema.safeParse(record);
    if (result.success) {
      valid.push(result.data);
    } else {
      invalid.push({ record, errors: result.error });
      logger.logInvalidRecord(entityType, record, result.error);
    }
  }

  return { valid, invalid };
}
